<template>
  <nav>
    <Logo class="hidden lg:block m-auto" />
    <ul v-if="user.status === 'SignedIn'">
      <li>
        <RouterLink @click="vibrate(5)" to="/" :aria-label="$t('nav.home')">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
            <g
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
            >
              <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
              <path
                d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
              />
            </g>
          </svg>
          <span class="label">{{ $t('nav.home') }}</span>
        </RouterLink>
      </li>
      <li>
        <RouterLink @click="vibrate(5)" to="/photos" :aria-label="$t('nav.photos')">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
            <g
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
            >
              <path d="M18 22H4a2 2 0 0 1-2-2V6" />
              <path d="m22 13l-1.296-1.296a2.41 2.41 0 0 0-3.408 0L11 18" />
              <circle cx="12" cy="8" r="2" />
              <rect width="16" height="16" x="6" y="2" rx="2" />
            </g>
          </svg>
          <span class="label">{{ $t('nav.photos') }}</span>
        </RouterLink>
      </li>
      <li>
        <RouterLink @click="vibrate(5)" to="/standings" :aria-label="$t('nav.standings')">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
            <!-- Icon from BoxIcons by Atisa - https://creativecommons.org/licenses/by/4.0/ -->
            <path
              fill="currentColor"
              d="M21 4h-3V3a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1v1H3a1 1 0 0 0-1 1v3c0 4.31 1.799 6.91 4.819 7.012A6 6 0 0 0 11 17.91V20H9v2h6v-2h-2v-2.09a6.01 6.01 0 0 0 4.181-2.898C20.201 14.91 22 12.31 22 8V5a1 1 0 0 0-1-1M4 8V6h2v6.83C4.216 12.078 4 9.299 4 8m8 8c-2.206 0-4-1.794-4-4V4h8v8c0 2.206-1.794 4-4 4m6-3.17V6h2v2c0 1.299-.216 4.078-2 4.83"
            />
          </svg>
          <span class="label">{{ $t('nav.standings') }}</span>
        </RouterLink>
      </li>
      <li>
        <RouterLink @click="vibrate(5)" to="/search" :aria-label="$t('nav.search')">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
            <!-- Icon from Lucide by Lucide Contributors - https://github.com/lucide-icons/lucide/blob/main/LICENSE -->
            <g
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21l-4.3-4.3" />
            </g>
          </svg>
          <span class="label">{{ $t('nav.search') }}</span>
        </RouterLink>
      </li>
      <li>
        <RouterLink @click="vibrate(5)" to="/notifications" :aria-label="$t('nav.notifications')">
          <div class="relative">
            <div
              v-if="notifications.unread > 0"
              class="notifications"
              :aria-label="$t('nav.unread_notifications')"
            >
              {{ notifications.unread > 9 ? '9+' : notifications.unread }}
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
              <path
                fill="none"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9m4.3 13a1.94 1.94 0 0 0 3.4 0"
              />
            </svg>
          </div>
          <span class="label">{{ $t('nav.notifications') }}</span>
        </RouterLink>
      </li>
      <li>
        <RouterLink
          @click="vibrate(5)"
          :to="{ name: 'profile-username', params: { username: user.data.username } }"
          :aria-label="$t('nav.profile')"
        >
          <img
            :src="user.status === 'SignedIn' ? user.data.avatar : '/img/default-indigo.png'"
            alt=""
            class="w-6 h-6 rounded-full aspect-square border-2"
            :class="{
              'border-blue-500': route.params.username === user.data.username,
              'border-stone-700 dark:border-stone-300': route.params.username !== user.data.username
            }"
          />
          <span class="label">{{ $t('nav.profile') }}</span>
        </RouterLink>
      </li>
    </ul>
    <ul v-else>
      <li>
        <RouterLink @click="vibrate(5)" to="/" :aria-label="$t('nav.home')">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
            <g
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
            >
              <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
              <path
                d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
              />
            </g>
          </svg>
          <span class="label">{{ $t('nav.home') }}</span>
        </RouterLink>
      </li>
      <li>
        <RouterLink @click="vibrate(5)" to="/login" :aria-label="$t('login.login_button')">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
            <!-- Icon from Lucide by Lucide Contributors - https://github.com/lucide-icons/lucide/blob/main/LICENSE -->
            <path
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4m-5-4l5-5l-5-5m5 5H3"
            />
          </svg>
          <span class="label">{{ $t('login.login_button') }}</span>
        </RouterLink>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import { useVibrate } from '@/composables/useVibrate'
import { useNotifications } from '@/stores/notifications'
import { useSupabase, type AuthState } from '@/stores/supabase'
import { useRoute } from 'vue-router'
import Logo from '@/components/Logo.vue'

const { vibrate } = useVibrate()
const notifications = useNotifications()
const route = useRoute()

defineProps<{
  user: AuthState
}>()
</script>

<style scoped>
nav {
  z-index: 9999;
  background: theme('colors.stone.100');
  position: fixed;
  bottom: 0;
  width: 100%;
  margin: 0 auto;
  border-top: 1px solid theme('colors.stone.700');

  .dark & {
    background: theme('colors.stone.900');
  }

  li svg {
    width: theme('spacing.6');
    height: theme('spacing.6');
  }

  @media (min-width: theme('screens.lg')) {
    background: transparent;
    position: sticky;
    top: 0;
    bottom: auto;
    /* width: 400px; */
    height: 100dvh;
    padding-top: theme('spacing.10');
    border-right: 1px solid theme('colors.stone.200');
    border-top: none;
    background: none;

    .dark & {
      border-right: 1px solid theme('colors.stone.800');
      background: none;
    }
  }

  ul {
    display: flex;
    justify-content: space-around;
    padding: theme('spacing.4');
    gap: theme('spacing.3');
    align-items: center;
    height: 100%;

    @media (min-width: theme('screens.lg')) {
      flex-direction: column;
      height: auto;
    }

    li {
      width: 100%;
      display: flex;
      justify-content: center;
      color: theme('colors.stone.700');
      transition: color 0.2s ease-in-out;

      .dark & {
        color: theme('colors.stone.300');
      }

      &:hover {
        color: theme('colors.stone.900');

        .dark & {
          color: theme('colors.stone.50');
        }
      }

      @media (min-width: theme('screens.lg')) {
        justify-content: flex-start;
        font-size: theme('fontSize.xl');
      }

      a {
        display: flex;
        gap: theme('spacing.4');
        align-items: center;

        .label {
          display: none;

          @media (min-width: theme('screens.lg')) {
            display: block;
          }
        }
      }

      .notifications {
        position: absolute;
        top: -0.5rem;
        right: -0.5rem;
        background: theme('colors.red.500');
        border-radius: theme('borderRadius.full');
        display: flex;
        justify-content: center;
        align-items: center;
        width: theme('size.5');
        height: theme('size.5');
        padding: theme('spacing.1');
        font-size: theme('fontSize.xs');
        color: theme('colors.stone.50');
        aspect-ratio: 1;
        font-weight: theme('fontWeight.bold');
      }
    }
  }
}
</style>
